import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { selectLang } from "../lang/langSlice";
import { formatMarkers, wellnessFormat } from "./format";

export const getWellnessMarkers = createAsyncThunk(
  "markers/getWellness",
  async (_, { rejectWithValue, getState }) => {
    const lang = selectLang(getState());
    return api
      .get(`wellness-questionnaire/?lang=${lang}`)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectWellnessMarkers(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

export const getSessionsMarkers = createAsyncThunk(
  "markers/getSessions",
  async (_, { rejectWithValue, getState }) => {
    const lang = selectLang(getState());
    return api
      .get(`session-questionnaire/?lang=${lang}`)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return rejectWithValue(err.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectSessionsMarkers(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

const initialState = {
  wellness: {
    status: "idle",
    error: null,
    data: {},
  },
  sessions: {
    status: "idle",
    error: null,
    data: {},
  },
};

const markersSlice = createSlice({
  name: "markers",
  initialState,
  reducers: {
    add: (state, action) => {
      const { questionnaireType, markerType, data } = action.payload;
      if (
        !state[questionnaireType].data[markerType].find((d) => d.id === data.id)
      ) {
        state[questionnaireType].data[markerType].push(data);
      }
    },
    set: (state, action) => {
      const { questionnaireType, markerType, data } = action.payload;
      state[questionnaireType].data[markerType] = state[questionnaireType].data[
        markerType
      ].map((d) => (d.id === data.id ? data : d));
    },
    delete: (state, action) => {
      const { questionnaireType, markerType, id } = action.payload;
      state[questionnaireType].data[markerType] = state[questionnaireType].data[
        markerType
      ].filter((d) => d.id !== id);
    },
    sort: (state, action) => {
      const { questionnaireType, data } = action.payload;
      state[questionnaireType].data = data;
    },
    addIncludedSession: (state, action) => {
      const { markerType, selectedMarkerIds, selectedSession } = action.payload;
      state.sessions.data[markerType] = state.sessions.data[markerType].map(
        (d) =>
          selectedMarkerIds.includes(d.id)
            ? {
                ...d,
                session_types: [...d.session_types, selectedSession],
              }
            : d
      );
    },
    deactiveTrainingLoadInOtherMarker: (state, action) => {
      const deactiveTrainingLoad = (markerType) => {
        return state.sessions.data[markerType].map((d) => ({
          ...d,
          training_load: d.id === action.payload ? d.training_load : false,
        }));
      };

      state.sessions.data.vas = deactiveTrainingLoad("vas");
      state.sessions.data.scale = deactiveTrainingLoad("scale");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWellnessMarkers.pending, (state) => {
        state.wellness.status = "pending";
        state.wellness.error = null;
      })
      .addCase(getWellnessMarkers.fulfilled, (state, action) => {
        state.wellness.status = "fulfilled";
        state.wellness.data = action.payload;
      })
      .addCase(getWellnessMarkers.rejected, (state, action) => {
        state.wellness.status = "rejected";
        state.wellness.error = action.payload;
      })
      .addCase(getSessionsMarkers.pending, (state) => {
        state.sessions.status = "pending";
        state.sessions.error = null;
      })
      .addCase(getSessionsMarkers.fulfilled, (state, action) => {
        state.sessions.status = "fulfilled";
        state.sessions.data = action.payload;
      })
      .addCase(getSessionsMarkers.rejected, (state, action) => {
        state.sessions.status = "rejected";
        state.sessions.error = action.payload;
      })
      .addCase("sessions/delete", (state, action) => {
        const deactiveSession = (markerType) => {
          return state.sessions.data[markerType].map((d) => ({
            ...d,
            session_types: d.session_types.map((s) =>
              s.id === action.payload ? { ...s, active: false } : s
            ),
          }));
        };

        state.sessions.data = {
          vas: deactiveSession("vas"),
          scale: deactiveSession("scale"),
          indicator: deactiveSession("indicator"),
          duration: deactiveSession("duration"),
          boolean: deactiveSession("boolean"),
          mcq: deactiveSession("mcq"),
          question: deactiveSession("question"),
        };
      })
      .addCase("trash/restoreSession", (state, action) => {
        const activeSession = (markerType) => {
          return state.sessions.data[markerType].map((d) => ({
            ...d,
            session_types: d.session_types.map((s) =>
              s.id === action.payload ? { ...s, active: true } : s
            ),
          }));
        };

        state.sessions.data = {
          vas: activeSession("vas"),
          scale: activeSession("scale"),
          indicator: activeSession("indicator"),
          duration: activeSession("duration"),
          boolean: activeSession("boolean"),
          mcq: activeSession("mcq"),
          question: activeSession("question"),
        };
      })
      .addCase("trash/deleteSession", (state, action) => {
        const activeSession = (markerType) => {
          return state.sessions.data[markerType].map((d) => ({
            ...d,
            session_types: d.session_types.filter(
              (s) => s.id !== action.payload
            ),
          }));
        };

        state.sessions.data = {
          vas: activeSession("vas"),
          scale: activeSession("scale"),
          indicator: activeSession("indicator"),
          duration: activeSession("duration"),
          boolean: activeSession("boolean"),
          mcq: activeSession("mcq"),
          question: activeSession("question"),
        };
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const markersActions = markersSlice.actions;

const _selectWellnessMarkers = (state) => state.markers.wellness;

export const selectWellnessMarkers = createSelector(
  [(state) => _selectWellnessMarkers(state)],
  (wellness) => {
    const data = wellness.data;
    const status = wellness.status;
    const error = wellness.error;
    const isLoading = status === "pending";

    const { perc, scale, quanti, duration, bool, mcq, qst, sorted } =
      formatMarkers(data);

    return {
      status,
      error,
      isLoading,
      items: wellnessFormat(sorted),
      perc: wellnessFormat(perc),
      scale: wellnessFormat(scale),
      quanti: wellnessFormat(quanti),
      duration: wellnessFormat(duration),
      bool: wellnessFormat(bool),
      mcq: wellnessFormat(mcq),
      qst: wellnessFormat(qst),
    };
  }
);

const _selectSessionsMarkers = (state) => state.markers.sessions;

export const selectSessionsMarkers = createSelector(
  [(state) => _selectSessionsMarkers(state)],
  (sessions) => {
    const data = sessions.data;
    const status = sessions.status;
    const error = sessions.error;
    const isLoading = status === "pending";

    const { perc, scale, quanti, duration, bool, mcq, qst, sorted } =
      formatMarkers(data);

    return {
      data,
      status,
      error,
      isLoading,
      items: sorted,
      perc,
      scale,
      quanti,
      duration,
      bool,
      mcq,
      qst,
    };
  }
);

function findTrainingLoadTrue(obj) {
  for (const key in obj) {
    const item = obj[key];
    const found = item.find((d) => d.training_load);
    if (found) return found;
  }
  // if (Array.isArray(obj)) {
  //   for (const item of obj) {
  //     const result = findTrainingLoadTrue(item);
  //     if (result) return result;
  //   }
  // } else if (typeof obj === "object" && obj !== null) {
  //   if (obj.training_load === true) {
  //     return obj;
  //   }
  //   for (const key in obj) {
  //     const result = findTrainingLoadTrue(obj[key]);
  //     if (result) return result;
  //   }
  // }
  // return null;
}

export const selectTrainingLoadMarker = createSelector(
  [(state) => selectSessionsMarkers(state).data],
  (sessionMarkers) => {
    return findTrainingLoadTrue(sessionMarkers);
  }
);

export const selectMarkerScale = createSelector(
  [
    selectSessionsMarkers,
    selectWellnessMarkers,
    (_, __, questType) => questType,
    (_, markerId) => markerId,
  ],
  ({ scale: sessionsScale }, { scale: wellnessScale }, questType, markerId) => {
    if (questType === "wellness") {
      return wellnessScale.find((marker) => marker.id === markerId) || {};
    } else {
      return sessionsScale.find((marker) => marker.id === markerId) || {};
    }
  }
);

export const selectMarkerMcq = createSelector(
  [
    selectSessionsMarkers,
    selectWellnessMarkers,
    (_, __, questType) => questType,
    (_, markerId) => markerId,
  ],
  ({ mcq: sessionsMcq }, { mcq: wellnessMcq }, questType, markerId) => {
    if (questType === "wellness") {
      return wellnessMcq.find((marker) => marker.id === markerId) || {};
    } else {
      return sessionsMcq.find((marker) => marker.id === markerId) || {};
    }
  }
);

export default markersSlice.reducer;
